import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomEventCommand, CustomPlaceCommand } from '../../../../commands/hub/custom.event.command';
import { VisitorSettingsCommand } from '../../../../commands/pagination.query.command';
import { ButtonTriggerEvent } from '../../../../common/components';
import { BaseComponent } from '../../../../common/components/base.component';
import { ConfirmModalComponent } from '../../../../common/components/confirm.modal/confirm.modal.component';
import {
  ConfirmModalActions,
  ConfirmModalData,
} from '../../../../common/components/confirm.modal/confirm.modal.provider';
import { ShareService } from '../../../../services/share.service';
import { VisitorSettingsStore } from '../../../../stores/visitor.calendar.store';
import { EditPlaceModalComponent } from '../../edit.place.modal/edit.place.modal.component';
import { EditPlaceModalActions } from '../../edit.place.modal/edit.place.modal.provider';
import { getRouteUrl, routeConfigs } from '../../../../common/constants/route.config';
import { AuthService } from '../../../../common/services/auth.service';
import { ModalService } from '../../../../common/services/modal.service';
import { HubPlace } from '../../../../models/hub/hub.place';
import { HubTour } from '../../../../models/hub/hub.tour';
import { HubApiService } from '../../../../services/hub.api.service';
import { ImageService } from '../../../../services/image.service';
import { DisplayingOn } from '../../side.bar/hub.side.bar.component';
import { HubCalendarComponent } from '../hub.calendar.component';
import { CustomizationApiService } from '../../../../services/customization.api.service';
import { EditorChoiceDataSource } from '../../../../models/hub/hub.event';
import { EditorChoiceStore } from '../../../../stores/hub.calendar.store';
import { PlaceService } from '../../../../services/place.service';
import { AlertifyService } from '../../../../services/alertify.service';
import { EditPlaceCategoryModalActions } from '../../edit.place.category.modal/edit.place.category.modal.provider';
import { EditPlaceCategoryModalComponent } from '../../edit.place.category.modal/edit.place.category.modal.component';
import { localStorageKeys } from '../../../../common/constants/local.storage.keys';
import { ShowPromptModalComponent } from '../../show.prompt.modal/show.prompt.modal.component';
import { PromptModalData, ShowPromptModalActions } from '../../show.prompt.modal/show.prompt.modal.provider';
import { clone } from '../../../../common/extensions';
import { KeywordService } from '../../../../services/keyword.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'ig-hub-place-card',
  templateUrl: './hub.place.card.component.html',
  styleUrls: ['./hub.place.card.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class HubPlaceCardComponent extends BaseComponent implements OnInit, OnDestroy {
  public hubName: string;
  public isOperating: boolean;
  public editorChoiceText = '';
  public addToEditorChoiceTextTip = 'Add to ';
  public removeFromEditorChoiceTextTip = 'Remove from ';
  public customSettings: VisitorSettingsCommand;
  @Input() public isItineraryOn: boolean;
  @Input() public place: HubPlace;
  @Input() public notAllowed: boolean = false;
  @Input() public searchFields: string[] = [];

  keywordSubscription: Subscription;
  keyword: string;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private modalService: ModalService,
    private authService: AuthService,
    private hubApiService: HubApiService,
    public imageService: ImageService,
    public editorChoiceStore: EditorChoiceStore,
    private customizationApiService: CustomizationApiService,
    private visitorSettingsCommand: VisitorSettingsStore,
    private alertify: AlertifyService,
    public parent: HubCalendarComponent,
    private keywordService: KeywordService
  ) {
    super({ activatedRoute });
  }

  public ngOnInit() {
    let { hubName } = this.allRouteParams;
    this.hubName = hubName;
    this.editorChoiceStore.infoCommand.takeUntil(this.isDestroyed).subscribe((editorChoice: EditorChoiceDataSource) => {
      this.editorChoiceText = editorChoice.editorChoiceForPlace;
      this.addToEditorChoiceTextTip += this.editorChoiceText;
      this.removeFromEditorChoiceTextTip += this.editorChoiceText;
    });

    this.visitorSettingsCommand.settingsCommand
      .takeUntil(this.isDestroyed)
      .subscribe((settings: VisitorSettingsCommand) => {
        this.customSettings = settings;
      });

      this.keywordSubscription = this.keywordService.keyword$.subscribe(k => this.keyword = k);
  }

  ngOnDestroy() {
    this.keywordSubscription.unsubscribe();
  }

  public showReadable(code: string) {
    return PlaceService.allToReadableCategory(code);
  }

  public showEditCategoryModal() {
    let actions = <EditPlaceCategoryModalActions>{
      confirm: (event: ButtonTriggerEvent, category) => {
        this.place.category = category.category;
        this.place.categoryList = category.categoryList;
        this.place.categoryText = category.categoryText;

        this.hubApiService.updatePlace(this.place.hubName, this.place._id, this.place).subscribe(() => { });
      },
    };

    this.modalService.show({
      component: EditPlaceCategoryModalComponent,
      providers: [
        { provide: EditPlaceCategoryModalActions, useValue: actions },
        { provide: HubPlace, useValue: this.place },
      ],
      panelClass: 'width-modal-70p',
    });
  }

  public approve() {
    if (this.notAllowed) {
      this.alertify.warning(`This place's category is not in the allowed list, please update it.`);
      return;
    }

    this.isOperating = true;
    this.hubApiService
      .approvePlace(this.hubName, this.place._id)
      .finally(() => (this.isOperating = false))
      .subscribe(() => {
        this.place.isApproved = true;
      });
  }

  public unApprove() {
    this.isOperating = true;
    this.hubApiService
      .unApprovePlace(this.hubName, this.place._id)
      .finally(() => (this.isOperating = false))
      .subscribe(() => {
        this.place.isApproved = false;
      });
  }

  public pin() {
    this.isOperating = true;
    this.hubApiService
      .pinPlace(this.hubName, this.place._id)
      .finally(() => (this.isOperating = false))
      .subscribe(() => {
        this.parent.pinPlace(this.place);
      });
  }

  public unPin() {
    this.isOperating = true;
    this.hubApiService
      .unPinPlace(this.hubName, this.place._id)
      .finally(() => (this.isOperating = false))
      .subscribe(() => {
        this.parent.unPinPlace(this.place);
      });
  }

  public choice() {
    this.isOperating = true;
    this.hubApiService
      .choicePlace(this.hubName, this.place._id)
      .finally(() => (this.isOperating = false))
      .subscribe(() => {
        this.place.isChoice = true;
      });
  }

  public unChoice() {
    this.isOperating = true;
    this.hubApiService
      .unChoicePlace(this.hubName, this.place._id)
      .finally(() => (this.isOperating = false))
      .subscribe(() => {
        this.place.isChoice = false;
      });
  }

  public custom() {
    this.isOperating = true;
    this.hubApiService
      .customPlace(this.hubName, this.place._id)
      .finally(() => (this.isOperating = false))
      .subscribe(() => {
        this.place.isCustom = true;
      });
  }

  public unCustom() {
    this.isOperating = true;
    this.hubApiService
      .unCustomPlace(this.hubName, this.place._id)
      .finally(() => (this.isOperating = false))
      .subscribe(() => {
        this.place.isCustom = false;
      });
  }

  public toBeExport() {
    this.isOperating = true;
    this.hubApiService
      .toBeExportPlace(this.hubName, this.place._id)
      .finally(() => (this.isOperating = false))
      .subscribe(() => {
        this.place.isToBeExport = true;
      });
  }

  public unToBeExport() {
    this.isOperating = true;
    this.hubApiService
      .unToBeExportPlace(this.hubName, this.place._id)
      .finally(() => (this.isOperating = false))
      .subscribe(() => {
        this.place.isToBeExport = false;
      });
  }

  public addOrRemoveToCustomBtn(btn: CustomPlaceCommand, index: number) {
    if (!this.isInCustomBtn(btn)) {
      this.hubApiService.addPlaceToCustomBtn(this.hubName, this.place._id, index).subscribe((customPlaces) => {
        this.parent.customBtns.places = customPlaces;
      });
    } else {
      this.hubApiService.removePlaceFromCustomBtn(this.hubName, this.place._id, index).subscribe((customPlaces) => {
        this.parent.customBtns.places = customPlaces;
      });
    }
  }

  public isInCustomBtn(btn?: CustomPlaceCommand) {
    if (btn) {
      return btn.places.any((x) => x === this.place._id);
    } else {
      let isIn = false;
      if (this.parent.customBtns.places) {
        for (let x of this.parent.customBtns.places) {
          if (x.places.any((x) => x === this.place._id)) {
            isIn = true;
            break;
          }
        }
      }
      return isIn;
    }
  }

  public showEditModal() {
    let actions = <EditPlaceModalActions>{
      confirm: (event: ButtonTriggerEvent, place) => {
        this.place = place;
        if (this.place.cover && this.place.cover.source) {
          this.place.cover.source = this.imageService.imageSourceUrl(this.place.cover.source);
        }
      },
    };

    this.modalService.show({
      component: EditPlaceModalComponent,
      providers: [
        { provide: EditPlaceModalActions, useValue: actions },
        { provide: HubPlace, useValue: clone(this.place) },
      ],
      panelClass: ['width-modal-4', 'ig-edit-place-modal', 'transition-50'],
    });
  }

  public block() {
    this.isOperating = true;

    let actions = <ConfirmModalActions>{
      confirm: (event: ButtonTriggerEvent) => {
        this.hubApiService
          .blockPlace(this.hubName, this.place._id)
          .finally(() => {
            event.complete();
            this.isOperating = false;
          })
          .subscribe(() => {
            this.parent.removePlace(this.place);
            const doNotShowModal = localStorage.getItem(localStorageKeys.doNotShowRemoveSuccessfullyModal);
            if (!doNotShowModal) {
              this.showRmoveSuccessModal();
            }
          });
      },
      cancel: () => {
        this.isOperating = false;
      },
    };

    let data = <ConfirmModalData>{
      confirmModalIcon: 'warning',
      confirmModalTitle: 'Alert',
      confirmModalDescription:
        'Are you sure you want to remove this place?',
      confirmModalConfirmButtonText: 'Remove',
      confirmModalCancelButtonText: 'Cancel',
      isAsync: true,
    };

    this.modalService.show({
      component: ConfirmModalComponent,
      providers: [
        { provide: ConfirmModalActions, useValue: actions },
        { provide: ConfirmModalData, useValue: data },
      ],
    });
  }

  public gotoWebsite() {
    if (this.place.hostLink) {
      window.open(this.place.hostLink, '_blank');
    }
  }

  public updateImage() {
    this.hubApiService
      .updateFBImage(this.place.hostLink, this.place._id, this.place.name || this.place.title)
      .subscribe((data) => {
        console.log(JSON.stringify(data));
      });
  }

  public showId() {
    ShareService.getFacebookPlaceId(this.place.hostLink);
  }

  public addOrRemoveFromTour(tour: HubTour) {
    if (!this.isInTour(tour)) {
      this.hubApiService
        .addAttractionToTour(this.hubName, { id: tour._id, feedType: 'place', feedId: this.place._id })
        .subscribe(() => {
          const currentTour = this.parent.tours.find(x => x._id === tour._id);
          if (currentTour) {
            currentTour.days[0].attractions.push({
              feedId: this.place._id,
              feedType: 'place',
            });
          }
          // this.hubApiService.getTours(this.hubName).subscribe((tours) => {
          //   this.parent.tours = tours;
          // });
        });
    } else {
      this.hubApiService
        .removeAttractionFromTour(this.hubName, {
          id: tour._id,
          attractionId: this.place._id,
          feedType: 'place',
        })
        .subscribe(() => {
          const currentTour = this.parent.tours.find((x) => x._id === tour._id);
          if (currentTour) {
            currentTour.days.forEach((day) => {
              day.attractions = day.attractions.filter((x) => x.feedId !== this.place._id);
            });
          }
          // this.hubApiService.getTours(this.hubName).subscribe((tours) => {
          //   this.parent.tours = tours;
          // });
        });
    }
  }

  public async gotoTours() {
    await this.router.navigateByUrl(
      getRouteUrl(routeConfigs.hub.tours, {
        hubName: this.hubName,
      })
    );
  }

  public showMarketing() {
    this.router.navigateByUrl(
      getRouteUrl(routeConfigs.hub.marketing, {
        hubName: this.hubName,
      })
    );
  }

  public isInTour(tour?: HubTour) {
     if (tour) {
      return tour.days.some((x) => x.attractions.some((x) => x.feedId === this.place._id));
    } else {
      if (!this.parent.tours) {
        return false;
      }
      return this.parent.tours.some(tour => 
        tour.days.some(day => 
          day.attractions.some(attraction => 
            attraction.feedId === this.place._id
          )
        )
      );
    }
  }

  private showRmoveSuccessModal() {
    this.modalService.show({
      component: ShowPromptModalComponent,
      providers: [
        { provide: ShowPromptModalActions, useValue: null },
        {
          provide: PromptModalData,
          useValue: new PromptModalData('Remove successfully', null, [
            'The selected item has been successfully removed from the dashboard.',
            'You can restore this item from the Removed List at any time.',
            'While in the Removed List, this item cannot be pulled again.'
          ]),
        },
      ],
      panelClass: 'width-modal-2',
    });
  }

  get isSearchName(): boolean {
    return this.searchFields.length === 0 || this.searchFields.indexOf('name') > -1;
  }

  get isSearchAddress(){
    return this.searchFields.indexOf('address') > -1;
  }

  get isSearchCategory(){
    return this.searchFields.indexOf('category') > -1;
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/ImGoing/IMGoing/web/src/components/hub/calendar/place.card/hub.place.card.component.ts